import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import { centsToDollars, isNoMaximumOOPDollars } from "helpers/price";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { PatientBenefits as PatientBenefitsDetails } from "pages/CreateOrder/components/PatientBenefitsSection/components/Benefits/components/PatientBenefits";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(2)};
  border: 1px solid var(--gray-new-200) !important;

  .p-card-body {
    padding-top: ${sizer(2)};
  }
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  input {
    width: 100%;
  }
`;

const StyledDialogContent = styled.div`
  /* Ensure content fills the dialog */
  width: 100%;
  overflow-x: hidden;

  /* Hide the unnecessary vertical scrollbar on the inner tab nav */
  .p-tabview-nav {
    overflow-y: hidden !important;
  }
`;

interface IPatientBenefits {
  deductible: number;
  outOfPocketMax: number;
  flatCopay: number;
  coinsurancePercent: number;
  deductible2: number;
  outOfPocketMax2: number;
  flatCopay2: number;
  coinsurancePercent2: number;
  payerName: string;
  payerName2: string;
  placeOfService: string;
  locality: string;
  memberId: string;
  memberId2: string;
  serviceType: string;
  serviceType2: string;
}

function PatientBenefits({ order }: { order: IOrder }) {
  const { control, setValue } = useFormContext<IPatientBenefits>();
  const payerName2Field = (order.serviceDetails as any)?.secondaryPayerName;
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const hasBenefitDetails = !!order?.benefitDetails;
  const hasSecondaryBenefitDetails = !!order?.secondaryBenefitDetails;
  const hasSecondaryInsurance = !!order?.secondaryPayer;

  useEffect(() => {
    setValue("payerName", (order.serviceDetails as any)?.payerName || "Any");
    setValue(
      "payerName2",
      (order.serviceDetails as any)?.secondaryPayerName || "Any"
    );
    setValue(
      "deductible",
      centsToDollars(order?.gfeQuote?.remainingDeductibleInCents || 0)
    );
    setValue(
      "outOfPocketMax",
      centsToDollars(order?.gfeQuote?.outOfPocketMaxInCents || 0)
    );
    setValue(
      "flatCopay",
      centsToDollars(order?.gfeQuote?.flatCopayInCents || 0)
    );
    setValue("coinsurancePercent", order?.gfeQuote?.coinsurancePercent || 0);
    setValue(
      "memberId",
      order?.benefitDetails?.request?.member?.memberID ||
        order?.patient?.patientBenefits?.memberID ||
        null
    );
    setValue(
      "serviceType",
      mapPayerNameToCodeForDropdown.find(
        (item: { value: string; name: string }) =>
          item.value ===
          (order?.benefitDetails?.request?.practiceTypeCode ||
            order?.serviceDetails?.practiceTypeCode)
      )?.name ?? "All Other Medical"
    );

    // Set secondary insurance values
    if (order?.gfeQuote?.secondaryPatientBenefits) {
      setValue(
        "memberId2",
        order?.secondaryBenefitDetails?.request?.member?.memberID ||
          order?.patient?.secondaryPatientBenefits?.memberID ||
          null
      );
      setValue(
        "serviceType2",
        mapPayerNameToCodeForDropdown.find(
          (item: { value: string; name: string }) =>
            item.value ===
            (order?.secondaryBenefitDetails?.request?.practiceTypeCode ||
              order?.serviceDetails?.secondaryPracticeTypeCode)
        )?.name ?? "All Other Medical"
      );
      setValue(
        "deductible2",
        centsToDollars(order.gfeQuote.secondaryPatientBenefits.deductible || 0)
      );
      setValue(
        "outOfPocketMax2",
        centsToDollars(
          order.gfeQuote.secondaryPatientBenefits.outOfPocketMax || 0
        )
      );
      setValue(
        "flatCopay2",
        centsToDollars(order.gfeQuote.secondaryPatientBenefits.flatCopay || 0)
      );
      setValue(
        "coinsurancePercent2",
        order.gfeQuote.secondaryPatientBenefits.coinsurancePercent || 0
      );
    }
  }, [order, setValue]);

  return (
    <StyledCard>
      <div className="flex justify-content-between align-items-center mb-3">
        <span className="p-card-title m-0">Patient Benefits</span>
        {hasBenefitDetails && (
          <Button
            className="p-button-text"
            onClick={() => setShowDetailsDialog(true)}
          >
            See Details
          </Button>
        )}
      </div>
      <Dialog
        header="Eligibility Benefits Details"
        visible={showDetailsDialog}
        onHide={() => setShowDetailsDialog(false)}
        style={{ width: "900px", maxWidth: "90vw" }}
        dismissableMask
        blockScroll
      >
        <StyledDialogContent>
          {hasSecondaryBenefitDetails ? (
            <TabView>
              <TabPanel header="Primary Benefits">
                <PatientBenefitsDetails
                  benefitDetailsField={order.benefitDetails}
                  readOnly
                  fullWidth
                />
              </TabPanel>
              <TabPanel header="Secondary Benefits">
                <PatientBenefitsDetails
                  benefitDetailsField={order.secondaryBenefitDetails}
                  readOnly
                  fullWidth
                />
              </TabPanel>
            </TabView>
          ) : (
            <PatientBenefitsDetails
              benefitDetailsField={order.benefitDetails}
              readOnly
              fullWidth
            />
          )}
        </StyledDialogContent>
      </Dialog>
      <div className="formgrid grid w-100">
        {hasSecondaryInsurance && (
          <div className="col-12 mb-3">
            <span className="text-base font-bold text-700">
              Primary Insurance
            </span>
          </div>
        )}
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="payerName">Payer Name</Label>

          <Controller
            name="payerName"
            control={control}
            render={({ field: { value } }) => (
              <InputText
                disabled
                data-testid="payerName"
                id="payerName"
                placeholder="Payer Name"
                value={value}
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="serviceType">Service Type</Label>

          <Controller
            name="serviceType"
            control={control}
            render={({ field: { value } }) => (
              <InputText
                disabled
                data-testid="serviceType"
                id="serviceType"
                placeholder="Service Type"
                value={value}
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="memberId">Member ID</Label>

          <Controller
            name="memberId"
            control={control}
            render={({ field: { value } }) => (
              <InputText
                disabled
                data-testid="memberId"
                id="memberId"
                placeholder="Member ID"
                value={value}
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-0 lg:col-3" />
        <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
          <Label htmlFor="deductible">Remaining Deductible</Label>

          <Controller
            name="deductible"
            control={control}
            render={({ field: { value } }) => (
              <StyledInputNumber
                disabled
                data-testid="deductible"
                id="deductible"
                placeholder="Remaining Deductible"
                value={value}
                currency="USD"
                locale="en-US"
                mode="currency"
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="outOfPocketMax">Remaining Out-of-pocket Max</Label>

          <Controller
            name="outOfPocketMax"
            control={control}
            render={({ field: { value } }) => (
              <StyledInputNumber
                disabled
                data-testid="outOfPocketMax"
                id="outOfPocketMax"
                placeholder={
                  isNoMaximumOOPDollars(value)
                    ? "No maximum"
                    : "Out of Pocket Max"
                }
                value={isNoMaximumOOPDollars(value) ? null : value}
                currency="USD"
                locale="en-US"
                mode="currency"
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
          <Label htmlFor="accountId">Flat Copay</Label>

          <Controller
            name="flatCopay"
            control={control}
            render={({ field: { value } }) => (
              <StyledInputNumber
                disabled
                data-testid="flatCopay"
                id="flatCopay"
                placeholder="Flat copay"
                value={value}
                currency="USD"
                locale="en-US"
                mode="currency"
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
          <Label htmlFor="coinsurancePercent">Coinsurance Percent</Label>

          <Controller
            name="coinsurancePercent"
            control={control}
            render={({ field: { value } }) => (
              <StyledInputNumber
                disabled
                suffix="%"
                data-testid="coinsurancePercent"
                id="coinsurancePercent"
                placeholder="Coinsurance Percent"
                value={value}
              />
            )}
          />
        </div>

        {hasSecondaryInsurance && (
          <>
            <div className="col-12 mt-3 mb-3">
              <span className="text-base font-bold text-700">
                Secondary Insurance
              </span>
            </div>
            {payerName2Field && (
              <div className="field flex flex-column col-12 md:col-4 lg:col-3">
                <Label htmlFor="payerName2">Secondary Payer</Label>

                <Controller
                  name="payerName2"
                  control={control}
                  render={({ field: { value } }) => (
                    <InputText
                      disabled
                      data-testid="payerName2"
                      id="payerName2"
                      placeholder="Secondary Payer"
                      value={value}
                    />
                  )}
                />
              </div>
            )}
            <div className="field flex flex-column col-12 md:col-4 lg:col-3">
              <Label htmlFor="serviceType2">Service Type</Label>

              <Controller
                name="serviceType2"
                control={control}
                render={({ field: { value } }) => (
                  <InputText
                    disabled
                    data-testid="serviceType2"
                    id="serviceType2"
                    placeholder="Service Type"
                    value={value}
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3">
              <Label htmlFor="memberId2">Member ID</Label>

              <Controller
                name="memberId2"
                control={control}
                render={({ field: { value } }) => (
                  <InputText
                    disabled
                    data-testid="memberId2"
                    id="memberId2"
                    placeholder="Member ID"
                    value={value}
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-0 lg:col-3" />
            <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
              <Label htmlFor="deductible2">Remaining Deductible</Label>

              <Controller
                name="deductible2"
                control={control}
                render={({ field: { value } }) => (
                  <StyledInputNumber
                    disabled
                    data-testid="deductible2"
                    id="deductible2"
                    placeholder="Remaining Deductible"
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
              <Label htmlFor="outOfPocketMax2">
                Remaining Out-of-pocket Max
              </Label>

              <Controller
                name="outOfPocketMax2"
                control={control}
                render={({ field: { value } }) => (
                  <StyledInputNumber
                    disabled
                    data-testid="outOfPocketMax2"
                    id="outOfPocketMax2"
                    placeholder={
                      isNoMaximumOOPDollars(value)
                        ? "No maximum"
                        : "Out of Pocket Max"
                    }
                    value={isNoMaximumOOPDollars(value) ? null : value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
              <Label htmlFor="flatCopay2">Flat Copay</Label>

              <Controller
                name="flatCopay2"
                control={control}
                render={({ field: { value } }) => (
                  <StyledInputNumber
                    disabled
                    data-testid="flatCopay2"
                    id="flatCopay2"
                    placeholder="Flat copay"
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
              <Label htmlFor="coinsurancePercent2">Coinsurance Percent</Label>

              <Controller
                name="coinsurancePercent2"
                control={control}
                render={({ field: { value } }) => (
                  <StyledInputNumber
                    disabled
                    suffix="%"
                    data-testid="coinsurancePercent2"
                    id="coinsurancePercent2"
                    placeholder="Coinsurance Percent"
                    value={value}
                  />
                )}
              />
            </div>
          </>
        )}
      </div>
    </StyledCard>
  );
}

export default PatientBenefits;
